/**
 * DISCOVERY PAGE
 * 
 * Features:
 * - Dupe Finder: Find similar polishes by color
 * - ISO Matchmaker: Connect wishlist with marketplace listings
 * - Color Explorer: Browse by color
 * 
 * Uses glassmorphic design to preserve rainbow background
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Search, 
  Palette, 
  Heart, 
  Sparkles, 
  Copy, 
  ShoppingBag,
  ChevronRight,
  Loader2,
  X,
  Users,
  Plus,
  Check,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, collectionGroup, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { PriorityColors, type PriorityKey } from '../constants/categoryColors';
import ColorSearchFilter from '../components/ColorSearchFilter';
import { PageHeader } from '../components/common';
import { slugify } from '../utils/polishSlug';

// Types
interface CollectionItem {
  id: string;
  userId: string;
  name: string;
  brand: string;
  itemType: string;
  colors?: string[];
  primaryColor?: string;
  photos?: { url: string; isDefault?: boolean; caption?: string }[];
  wishlistPriority?: 'iso' | 'diso' | 'unicorn';
  onWishlist?: boolean;
  isForDestash?: boolean;
  destashPrice?: number;
  destashStatus?: string;
  colorDescription?: string;
  finish?: string[];
  holoType?: string[];
  colorShift?: string;
  particles?: string[];
  specialFeatures?: string[];
  colorFamily?: string[];
  collectionName?: string;
  releaseYear?: number;
}

interface DupeResult {
  item: CollectionItem;
  similarity: number;
  collectorCount: number;
}

// Color distance calculation (simple Euclidean in RGB space)
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

function colorDistance(hex1: string, hex2: string): number {
  const c1 = hexToRgb(hex1);
  const c2 = hexToRgb(hex2);
  if (!c1 || !c2) return Infinity;
  
  // Weighted Euclidean distance (human eye is more sensitive to green)
  const rMean = (c1.r + c2.r) / 2;
  const dR = c1.r - c2.r;
  const dG = c1.g - c2.g;
  const dB = c1.b - c2.b;
  
  return Math.sqrt(
    (2 + rMean / 256) * dR * dR +
    4 * dG * dG +
    (2 + (255 - rMean) / 256) * dB * dB
  );
}

function colorSimilarity(hex1: string, hex2: string): number {
  const distance = colorDistance(hex1, hex2);
  // Max distance is ~764, normalize to 0-100%
  return Math.max(0, 100 - (distance / 764) * 100);
}

/**
 * Map a hex color to the small set of color-family strings items are tagged
 * with (via the form's color-family multi-select). Mirrors the mobile
 * app's inferColorFamily so the ColorExplorer's server-side filter can
 * pre-narrow the query to the relevant families before the client does
 * fine-grained similarity math. Returns 1–3 families — the picked hex's
 * primary bucket plus neighbors, so borderline hues don't miss valid
 * matches from an adjacent family.
 */
function inferColorFamily(hex: string): string[] {
  const rgb = hexToRgb(hex);
  if (!rgb) return [];
  const { r, g, b } = rgb;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2 / 255;
  const saturation = max === min
    ? 0
    : (max - min) / (max + min < 255 ? max + min : 510 - max - min);

  if (saturation < 0.15) {
    if (lightness < 0.15) return ['black', 'neutral'];
    if (lightness > 0.85) return ['white', 'neutral'];
    return ['neutral', 'white', 'black'];
  }

  let hue = 0;
  if (max === r) hue = ((g - b) / (max - min)) % 6;
  else if (max === g) hue = (b - r) / (max - min) + 2;
  else hue = (r - g) / (max - min) + 4;
  hue = (hue * 60 + 360) % 360;

  if (hue < 15 || hue >= 345) return ['red', 'pink', 'orange'];
  if (hue < 45) return ['orange', 'red', 'brown'];
  if (hue < 65) return ['yellow', 'orange', 'gold'];
  if (hue < 165) return ['green', 'yellow'];
  if (hue < 200) return ['green', 'blue'];
  if (hue < 260) return ['blue', 'purple'];
  if (hue < 300) return ['purple', 'pink', 'blue'];
  return ['pink', 'purple', 'red'];
}

// ============================================
// DISCOVERY ITEM MODAL
// Shows item details and lets user add to their wishlist
// ============================================
interface DiscoveryItemModalProps {
  item: CollectionItem;
  similarity?: number;
  collectorCount?: number;
  onClose: () => void;
}

function DiscoveryItemModal({ item, similarity, collectorCount, onClose }: DiscoveryItemModalProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<'iso' | 'diso' | 'unicorn'>('iso');

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const getDefaultPhoto = () => {
    if (!item.photos?.length) return null;
    return item.photos.find(p => p.isDefault)?.url || item.photos[0]?.url;
  };

  const handleAddToWishlist = async () => {
    const user = getCurrentUser();
    if (!user) {
      alert('Please sign in to add items to your wishlist');
      return;
    }

    setIsAdding(true);
    try {
      // Carry the default photo over so the wishlist tile has visual
      // grounding — users complained the previous behavior ("Don't copy
      // photos - user can add their own") produced blank tiles for every
      // wishlist add. DMCA source is stamped so the community origin is
      // traceable if a takedown ever comes through.
      const carryPhotoUrl = getDefaultPhoto() ?? null;
      const photosToWrite = carryPhotoUrl
        ? [{
            url: carryPhotoUrl,
            isDefault: true,
            uploadedBy: user.uid,
            uploadedAt: new Date().toISOString(),
            source: 'community' as const,
            sourceUrl: carryPhotoUrl,
            dmcaStatus: 'active' as const,
          }]
        : [];
      // Create a new wishlist item in the user's collection
      const newItemRef = doc(collection(db, 'users', user.uid, 'items'));
      await setDoc(newItemRef, {
        name: item.name,
        // Lowercase/slug mirrors match what other item-creation paths
        // (AddPolishModal, CreationDetailModal wishlist adds) already
        // write. Keeps this item indexable by the same brandSlug +
        // nameSlug queries collection-group filters use.
        nameLower: (item.name ?? '').toLowerCase(),
        nameSlug: slugify(item.name ?? ''),
        brand: item.brand,
        brandLower: (item.brand ?? '').toLowerCase(),
        brandSlug: slugify(item.brand ?? ''),
        itemType: 'polish',
        colorDescription: item.colorDescription || '',
        primaryColor: item.primaryColor || null,
        colors: item.colors || [],
        finish: item.finish || [],
        holoType: item.holoType || [],
        colorShift: item.colorShift || null,
        particles: item.particles || [],
        specialFeatures: item.specialFeatures || [],
        colorFamily: item.colorFamily || [],
        collectionName: item.collectionName || '',
        releaseYear: item.releaseYear || null,
        onWishlist: true,
        isoPriority: selectedPriority,
        photos: photosToWrite,
        dateAdded: serverTimestamp(),
        createdAt: serverTimestamp(),
      });

      setAdded(true);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      alert('Failed to add to wishlist. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  // Gather all tags for display
  const allTags: string[] = [
    ...(item.finish || []),
    ...(item.holoType || []),
    ...(item.colorShift ? [item.colorShift] : []),
    ...(item.particles || []),
    ...(item.specialFeatures || []),
  ];

  const photoUrl = getDefaultPhoto();

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[5vh] pb-4 px-2 md:px-4 z-[100] overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-slate-800/95 border border-slate-600/50 rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-slate-600/50">
          <div className="flex-1 min-w-0 pr-2">
            <h2 className="text-lg font-bold text-white font-body truncate">{item.name}</h2>
            <Link 
              to={`/brand/${encodeURIComponent(item.brand)}`}
              className="text-sm text-slate-300 hover:text-purple-400 transition-colors flex items-center gap-1"
              onClick={onClose}
            >
              {item.brand}
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Photo */}
          {photoUrl ? (
            <div className="mb-4 flex justify-center bg-slate-900/50 rounded-xl p-2 relative">
              <img
                src={photoUrl}
                alt={`${item.brand} ${item.name}`}
                className="max-h-64 max-w-full object-contain rounded-lg"
              />
              {/* Collector attribution — this modal shows a specific
                  collector's photo of the polish, and the user might mistake
                  it for an official brand image without context. Bigger
                  badge than tile version since space allows. */}
              <span
                className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900/85 border border-slate-500/50 text-[11px] font-semibold text-slate-100"
                title="Collector photo — not an official brand image"
                aria-label="Collector photo — not an official brand image"
              >
                <Users className="w-3 h-3" />
                Collector photo
              </span>
            </div>
          ) : item.primaryColor ? (
            <div 
              className="w-full h-48 rounded-xl mb-4 flex items-center justify-center"
              style={{ backgroundColor: item.primaryColor }}
            >
              <Sparkles className="w-10 h-10 text-white/50" />
            </div>
          ) : (
            <div className="w-full h-48 rounded-xl mb-4 flex items-center justify-center bg-slate-700/50">
              <Sparkles className="w-10 h-10 text-slate-500" />
            </div>
          )}

          {/* Stats row */}
          {(similarity || collectorCount) && (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {similarity && (
                <span className="px-2.5 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-xs font-medium">
                  {Math.round(similarity)}% match
                </span>
              )}
              {collectorCount && (
                <span className="px-2.5 py-1 bg-slate-700/60 text-slate-300 rounded-full text-xs flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {collectorCount} collector{collectorCount !== 1 ? 's' : ''} {collectorCount === 1 ? 'has' : 'have'} this
                </span>
              )}
            </div>
          )}

          {/* Color Description */}
          {item.colorDescription && (
            <div className="mb-3">
              <p className="text-slate-300 italic text-sm">"{item.colorDescription}"</p>
            </div>
          )}

          {/* Color swatches */}
          {item.colors && item.colors.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-slate-400 mb-1">Sampled Colors:</p>
              <div className="flex gap-2">
                {item.colors.map((color, idx) => (
                  <div
                    key={idx}
                    className="w-7 h-7 rounded border-2 border-white/50 shadow"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {allTags.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-slate-400 mb-1">Characteristics:</p>
              <div className="flex flex-wrap gap-1">
                {allTags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 bg-slate-700/60 text-slate-300 rounded text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Color Families */}
          {item.colorFamily && item.colorFamily.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-slate-400 mb-1">Color Family:</p>
              <div className="flex flex-wrap gap-1">
                {item.colorFamily.map((color, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs"
                  >
                    {color}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Collection/Release info */}
          {(item.collectionName || item.releaseYear) && (
            <div className="mb-3 text-xs text-slate-400">
              {item.collectionName && <span>Collection: {item.collectionName}</span>}
              {item.collectionName && item.releaseYear && <span> • </span>}
              {item.releaseYear && <span>{item.releaseYear}</span>}
            </div>
          )}
        </div>

        {/* Footer - Add to Wishlist */}
        <div className="p-4 border-t border-slate-600/50 bg-slate-900/30 rounded-b-2xl">
          {added ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="flex items-center justify-center gap-2 text-green-400">
                <Check className="w-5 h-5" />
                <span className="font-semibold text-sm">Added to your wishlist!</span>
              </div>
              <Link
                to="/my-wants"
                className="text-sm text-purple-300 hover:text-purple-200 underline"
              >
                View my wishlist →
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-400 text-center">Add to your wishlist:</p>
              
              {/* Priority selector */}
              <div className="flex justify-center gap-2">
                {(['iso', 'diso', 'unicorn'] as const).map((priority) => {
                  const colors = PriorityColors[priority];
                  return (
                    <button
                      key={priority}
                      onClick={() => setSelectedPriority(priority)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedPriority === priority
                          ? `${colors.bg} text-white ring-2 ring-offset-1 ring-offset-slate-800`
                          : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600/60'
                      }`}
                    >
                      {colors.label}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleAddToWishlist}
                disabled={isAdding}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-all"
              >
                {isAdding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add to Wishlist
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function DiscoveryPage() {
  const [activeTab, setActiveTab] = useState<'dupes' | 'iso' | 'color'>('dupes');
  
  return (
    <div className="min-h-screen p-4 md:p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <PageHeader
          icon={Sparkles}
          title="Discover"
          subtitle="Find dupes, match your wishlist, and explore by color"
        />
      </div>

      {/* Tab Navigation */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="glass-card p-2">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('dupes')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold font-body transition-all ${
                activeTab === 'dupes'
                  ? 'bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white shadow-lg'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
              }`}
            >
              <Copy className="icon-md text-current" />
              <span className="hidden sm:inline">Dupe Finder</span>
              <span className="sm:hidden">Dupes</span>
            </button>
            <button
              onClick={() => setActiveTab('iso')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold font-body transition-all ${
                activeTab === 'iso'
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
              }`}
            >
              <ShoppingBag className="icon-md text-current" />
              <span className="hidden sm:inline">ISO Matchmaker</span>
              <span className="sm:hidden">ISO</span>
            </button>
            <button
              onClick={() => setActiveTab('color')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold font-body transition-all ${
                activeTab === 'color'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
              }`}
            >
              <Palette className="icon-md text-current" />
              <span className="hidden sm:inline">Color Explorer</span>
              <span className="sm:hidden">Color</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-6xl mx-auto">
        {activeTab === 'dupes' && <DupeFinderTab />}
        {activeTab === 'iso' && <ISOMatchmakerTab />}
        {activeTab === 'color' && <ColorExplorerTab />}
      </div>
    </div>
  );
}

// ============================================
// DUPE FINDER TAB
// ============================================
function DupeFinderTab() {
  const [brandQuery, setBrandQuery] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<CollectionItem | null>(null);
  const [searchResults, setSearchResults] = useState<CollectionItem[]>([]);
  const [dupeResults, setDupeResults] = useState<DupeResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFindingDupes, setIsFindingDupes] = useState(false);
  const [modalItem, setModalItem] = useState<DupeResult | null>(null);

  // Search for items by brand and/or name
  const handleSearch = async () => {
    const brandTerm = brandQuery.trim().toLowerCase();
    const nameTerm = nameQuery.trim().toLowerCase();

    // Need at least one field with 2+ characters to search
    if (brandTerm.length < 2 && nameTerm.length < 2) return;

    setIsSearching(true);
    setSelectedItem(null);
    setDupeResults([]);

    try {
      const currentUser = getCurrentUser();
      const itemsRef = collectionGroup(db, 'items');
      const q = query(itemsRef, where('itemType', '==', 'polish'));
      const snapshot = await getDocs(q);

      const results: CollectionItem[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Skip private items belonging to other users — the owner still
        // gets their own private items back in search results, but nobody
        // else sees them in a cross-user Dupe Finder scan.
        if (data.isPrivate === true && data.userId !== currentUser?.uid) return;
        const name = (data.name || '').toLowerCase();
        const brand = (data.brand || '').toLowerCase();

        // Only check a field if the user actually typed something in it.
        // If both fields are filled, BOTH must match (narrower, more precise search).
        const brandMatches = brandTerm.length < 2 || brand.includes(brandTerm);
        const nameMatches = nameTerm.length < 2 || name.includes(nameTerm);

        if (brandMatches && nameMatches && data.colors?.length > 0) {
          results.push({
            id: doc.id,
            userId: data.userId,
            ...data
          } as CollectionItem);
        }
      });

      // Deduplicate by name+brand, keep one with best photo
      const uniqueMap = new Map<string, CollectionItem>();
      results.forEach(item => {
        const key = `${item.brand.toLowerCase()}-${item.name.toLowerCase()}`;
        const existing = uniqueMap.get(key);
        if (!existing || (item.photos?.length && !existing.photos?.length)) {
          uniqueMap.set(key, item);
        }
      });

      setSearchResults(Array.from(uniqueMap.values()).slice(0, 20));
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Find dupes for selected item
  const findDupes = async (item: CollectionItem) => {
    setSelectedItem(item);
    setIsFindingDupes(true);
    setSearchResults([]);

    try {
      const targetColor = item.primaryColor || item.colors?.[0];
      if (!targetColor) {
        setDupeResults([]);
        return;
      }

      const currentUser = getCurrentUser();
      const itemsRef = collectionGroup(db, 'items');
      const q = query(itemsRef, where('itemType', '==', 'polish'));
      const snapshot = await getDocs(q);

      const dupes: Map<string, { item: CollectionItem; similarity: number; collectors: Set<string> }> = new Map();

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Owner-private items belonging to other users are invisible to
        // dupe finding — matches the visibility rule everywhere else. The
        // signed-in user's own private items still count so they can find
        // dupes within their own collection.
        if (data.isPrivate === true && data.userId !== currentUser?.uid) return;
        if (!data.colors?.length) return;

        // Skip the same item
        const itemKey = `${data.brand?.toLowerCase()}-${data.name?.toLowerCase()}`;
        const selectedKey = `${item.brand.toLowerCase()}-${item.name.toLowerCase()}`;
        if (itemKey === selectedKey) return;

        const itemColor = data.primaryColor || data.colors[0];
        const similarity = colorSimilarity(targetColor, itemColor);

        if (similarity >= 70) { // At least 70% similar
          const existing = dupes.get(itemKey);
          if (existing) {
            existing.collectors.add(data.userId);
            if (similarity > existing.similarity) {
              existing.similarity = similarity;
              existing.item = { id: doc.id, userId: data.userId, ...data } as CollectionItem;
            }
          } else {
            dupes.set(itemKey, {
              item: { id: doc.id, userId: data.userId, ...data } as CollectionItem,
              similarity,
              collectors: new Set([data.userId])
            });
          }
        }
      });

      const results: DupeResult[] = Array.from(dupes.values())
        .map(d => ({ item: d.item, similarity: d.similarity, collectorCount: d.collectors.size }))
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 20);

      setDupeResults(results);
    } catch (error) {
      console.error('Dupe finder error:', error);
    } finally {
      setIsFindingDupes(false);
    }
  };

  const getDefaultPhoto = (item: CollectionItem) => {
    if (!item.photos?.length) return null;
    return item.photos.find(p => p.isDefault)?.url || item.photos[0]?.url;
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-fuchsia-500 to-purple-500 rounded-xl">
          <Copy className="icon-base text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-glass font-body">
            Dupe Finder
          </h2>
          <p className="text-sm text-glass-secondary">Find similar polishes by color match</p>
        </div>
      </div>

      {/* Search Input */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
          <input
            type="text"
            value={brandQuery}
            onChange={(e) => setBrandQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Brand"
            className="w-full pl-10 pr-4 py-3 bg-slate-800/70 border border-slate-600 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent text-glass placeholder:text-glass-muted font-body"
          />
        </div>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
          <input
            type="text"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Polish name"
            className="w-full pl-10 pr-4 py-3 bg-slate-800/70 border border-slate-600 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent text-glass placeholder:text-glass-muted font-body"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={isSearching || (brandQuery.trim().length < 2 && nameQuery.trim().length < 2)}
          className="px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 transition-all"
        >
          {isSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
        </button>
      </div>

      {/* Selected Item */}
      {selectedItem && (
        <div className="mb-6 p-4 bg-slate-800/60 rounded-xl border border-slate-600">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-glass-secondary">Finding dupes for:</span>
            <button onClick={() => { setSelectedItem(null); setDupeResults([]); }} className="text-glass-muted hover:text-glass-secondary">
              <X className="icon-md" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            {getDefaultPhoto(selectedItem) ? (
              <img src={getDefaultPhoto(selectedItem)!} alt={selectedItem.name} className="w-16 h-16 rounded-lg object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-lg flex items-center justify-center" style={{ backgroundColor: selectedItem.primaryColor || '#ccc' }}>
                <Sparkles className="icon-base text-white/70" />
              </div>
            )}
            <div>
              <h3 className="font-bold text-glass">{selectedItem.name}</h3>
              <p className="text-glass-secondary">{selectedItem.brand}</p>
            </div>
            {selectedItem.primaryColor && (
              <div className="ml-auto flex items-center gap-2">
                <div className="w-8 h-8 rounded-full border-2 border-white shadow" style={{ backgroundColor: selectedItem.primaryColor }} />
                <span className="text-sm text-glass-muted font-mono">{selectedItem.primaryColor}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-glass-secondary mb-3">Select a polish to find dupes:</h3>
          <div className="grid gap-2 max-h-80 overflow-y-auto">
            {searchResults.map(item => (
              <button
                key={`${item.userId}-${item.id}`}
                onClick={() => findDupes(item)}
                className="flex items-center gap-3 p-3 bg-slate-800/60 hover:bg-slate-700 rounded-xl text-left transition-all group"
              >
                {getDefaultPhoto(item) ? (
                  <img src={getDefaultPhoto(item)!} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: item.primaryColor || '#ccc' }}>
                    <Sparkles className="icon-sm text-white/70" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-glass truncate">{item.name}</h4>
                  <p className="text-sm text-glass-muted truncate">{item.brand}</p>
                </div>
                {item.primaryColor && (
                  <div className="w-6 h-6 rounded-full border border-white shadow" style={{ backgroundColor: item.primaryColor }} />
                )}
                <ChevronRight className="icon-md text-glass-muted group-hover:text-glass" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading Dupes */}
      {isFindingDupes && (
        <div className="flex flex-col items-center justify-center py-12 text-glass-muted">
          <Loader2 className="w-8 h-8 animate-spin mb-3 text-glass" />
          <p>Analyzing colors and finding matches...</p>
        </div>
      )}

      {/* Dupe Results */}
      {!isFindingDupes && dupeResults.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-glass-secondary mb-3">
            Found {dupeResults.length} potential dupe{dupeResults.length !== 1 ? 's' : ''}: <span className="text-glass-muted">(click to view details)</span>
          </h3>
          <div className="grid gap-3">
            {dupeResults.map((dupe, index) => (
              <button
                key={`${dupe.item.userId}-${dupe.item.id}`}
                onClick={() => setModalItem(dupe)}
                className="flex items-center gap-4 p-4 bg-slate-800/60 rounded-xl border border-slate-600 hover:bg-slate-700/60 hover:border-slate-500 transition-all text-left w-full"
              >
                <div className="text-lg font-bold text-glass w-8">#{index + 1}</div>
                {getDefaultPhoto(dupe.item) ? (
                  <img src={getDefaultPhoto(dupe.item)!} alt={dupe.item.name} className="w-14 h-14 rounded-lg object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: dupe.item.primaryColor || '#ccc' }}>
                    <Sparkles className="icon-md text-white/70" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-glass">{dupe.item.name}</h4>
                  <p className="text-sm text-glass-muted">{dupe.item.brand}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-slate-700 text-glass rounded-full font-medium">
                      {Math.round(dupe.similarity)}% match
                    </span>
                    <span className="text-xs text-glass-muted flex items-center gap-1">
                      <Users className="icon-xs" /> {dupe.collectorCount} collector{dupe.collectorCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                {dupe.item.primaryColor && (
                  <div className="w-8 h-8 rounded-full border-2 border-white shadow" style={{ backgroundColor: dupe.item.primaryColor }} />
                )}
                <ChevronRight className="w-5 h-5 text-glass-muted" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {!isFindingDupes && selectedItem && dupeResults.length === 0 && (
        <div className="text-center py-12 text-glass-muted">
          <Copy className="icon-2xl mx-auto mb-3 opacity-30" />
          <p>No close color matches found.</p>
          <p className="text-sm">This polish might be truly unique!</p>
        </div>
      )}

      {/* Empty State */}
      {!selectedItem && searchResults.length === 0 && !isSearching && (
        <div className="text-center py-12 text-glass-muted">
          <Copy className="icon-2xl mx-auto mb-3 opacity-30" />
          <p>Search for a polish to find color dupes</p>
          <p className="text-sm">We'll analyze sampled colors to find matches</p>
        </div>
      )}

      {/* Detail Modal */}
      {modalItem && (
        <DiscoveryItemModal
          item={modalItem.item}
          similarity={modalItem.similarity}
          collectorCount={modalItem.collectorCount}
          onClose={() => setModalItem(null)}
        />
      )}
    </div>
  );
}

// ============================================
// ISO MATCHMAKER TAB
// ============================================
function ISOMatchmakerTab() {
  const [wishlistItems, setWishlistItems] = useState<CollectionItem[]>([]);
  const [matches, setMatches] = useState<Map<string, CollectionItem[]>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchingMatches, setIsSearchingMatches] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Track if search has been run

  // Load user's wishlist
  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = async () => {
    const user = getCurrentUser();
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      const itemsRef = collection(db, 'users', user.uid, 'items');
      const q = query(itemsRef, where('onWishlist', '==', true));
      const snapshot = await getDocs(q);

      const items: CollectionItem[] = snapshot.docs.map(doc => ({
        id: doc.id,
        userId: user.uid,
        ...doc.data()
      } as CollectionItem));

      // Sort by priority
      const priorityOrder = { unicorn: 0, diso: 1, iso: 2 };
      items.sort((a, b) => {
        const aPriority = priorityOrder[a.wishlistPriority || 'iso'] ?? 3;
        const bPriority = priorityOrder[b.wishlistPriority || 'iso'] ?? 3;
        return aPriority - bPriority;
      });

      setWishlistItems(items);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Search for marketplace matches
  const findMatches = async () => {
    if (wishlistItems.length === 0) return;
    
    setIsSearchingMatches(true);
    const user = getCurrentUser();

    try {
      const itemsRef = collectionGroup(db, 'items');
      const q = query(
        itemsRef,
        where('isForDestash', '==', true),
        where('destashStatus', '==', 'available')
      );
      const snapshot = await getDocs(q);

      const newMatches = new Map<string, CollectionItem[]>();

      wishlistItems.forEach(wishlistItem => {
        const wishlistKey = `${wishlistItem.brand.toLowerCase()}-${wishlistItem.name.toLowerCase()}`;
        const matchingListings: CollectionItem[] = [];

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          // Don't show user's own listings
          if (data.userId === user?.uid) return;

          const listingKey = `${(data.brand || '').toLowerCase()}-${(data.name || '').toLowerCase()}`;
          
          if (listingKey === wishlistKey) {
            matchingListings.push({
              id: doc.id,
              userId: data.userId,
              ...data
            } as CollectionItem);
          }
        });

        if (matchingListings.length > 0) {
          newMatches.set(wishlistItem.id, matchingListings);
        }
      });

      setMatches(newMatches);
    } catch (error) {
      console.error('Error finding matches:', error);
    } finally {
      setIsSearchingMatches(false);
      setHasSearched(true);
    }
  };

  const getPriorityBadge = (priority?: string) => {
    const key = (priority || 'iso') as PriorityKey;
    const colors = PriorityColors[key] || PriorityColors.iso;
    return <span className={`px-2 py-0.5 ${colors.bg} text-white text-xs rounded-full`}>{colors.label}</span>;
  };

  const getDefaultPhoto = (item: CollectionItem) => {
    if (!item.photos?.length) return null;
    return item.photos.find(p => p.isDefault)?.url || item.photos[0]?.url;
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl">
            <ShoppingBag className="icon-base text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-glass font-body">
              ISO Matchmaker
            </h2>
            <p className="text-sm text-glass-secondary">Find your wishlist items on the marketplace</p>
          </div>
        </div>
        <button
          onClick={findMatches}
          disabled={isSearchingMatches || wishlistItems.length === 0}
          className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 transition-all flex items-center gap-2"
        >
          {isSearchingMatches ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="icon-sm" />}
          Find Matches
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
        </div>
      ) : wishlistItems.length === 0 ? (
        <div className="text-center py-12 text-glass-muted">
          <Heart className="icon-2xl mx-auto mb-3 opacity-30" />
          <p>Your wishlist is empty</p>
          <p className="text-sm">Add items to your wishlist to find marketplace matches!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Stats */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 p-4 bg-slate-800/60 rounded-xl text-center">
              <div className="text-2xl font-bold text-glass">{wishlistItems.length}</div>
              <div className="text-sm text-glass-muted">Wishlist Items</div>
            </div>
            <div className="flex-1 p-4 bg-slate-800/60 rounded-xl text-center">
              <div className="text-2xl font-bold text-green-600">{matches.size}</div>
              <div className="text-sm text-glass-muted">Items with Matches</div>
            </div>
            <div className="flex-1 p-4 bg-slate-800/60 rounded-xl text-center">
              <div className="text-2xl font-bold text-pink-600">
                {Array.from(matches.values()).reduce((sum, arr) => sum + arr.length, 0)}
              </div>
              <div className="text-sm text-glass-muted">Total Listings</div>
            </div>
          </div>

          {/* No matches found message */}
          {hasSearched && matches.size === 0 && (
            <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
              <p className="text-amber-300 font-medium">No marketplace listings found for your wishlist items</p>
              <p className="text-sm text-glass-muted mt-1">Check back later — new listings appear all the time!</p>
            </div>
          )}

          {/* PublicWishlist with matches */}
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {wishlistItems.map(item => {
              const itemMatches = matches.get(item.id) || [];
              const hasMatch = itemMatches.length > 0;

              return (
                <div
                  key={item.id}
                  className={`p-4 rounded-xl border transition-all ${
                    hasMatch 
                      ? 'bg-green-50/70 border-green-300' 
                      : 'bg-slate-800/60 border-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {getDefaultPhoto(item) ? (
                      <img src={getDefaultPhoto(item)!} alt={item.name} className="w-14 h-14 rounded-lg object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center">
                        <Sparkles className="icon-md text-glass-muted" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-glass truncate">{item.name}</h4>
                        {getPriorityBadge(item.wishlistPriority)}
                      </div>
                      <p className="text-sm text-glass-muted">{item.brand}</p>
                    </div>
                    {hasMatch && (
                      <div className="text-green-600 font-semibold flex items-center gap-1">
                        <ShoppingBag className="icon-sm" />
                        {itemMatches.length} listing{itemMatches.length !== 1 ? 's' : ''}!
                      </div>
                    )}
                  </div>

                  {/* Matching Listings */}
                  {hasMatch && (
                    <div className="mt-4 pl-4 border-l-2 border-green-300 space-y-2">
                      {itemMatches.map(listing => (
                        <div key={`${listing.userId}-${listing.id}`} className="flex items-center gap-3 p-2 bg-slate-800/70 rounded-lg">
                          {getDefaultPhoto(listing) && (
                            <img src={getDefaultPhoto(listing)!} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-glass-secondary">Available for ${listing.destashPrice?.toFixed(2)}</p>
                          </div>
                          <button className="px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-default">
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// COLOR EXPLORER TAB
// ============================================
function ColorExplorerTab() {
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [searchColor, setSearchColor] = useState<string | null>(null); // The color we actually searched for
  const [similarity, setSimilarity] = useState(70); // Adjustable threshold
  const [searchSimilarity, setSearchSimilarity] = useState(70); // The similarity we searched with
  const [results, setResults] = useState<DupeResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [modalItem, setModalItem] = useState<DupeResult | null>(null);

  const searchByColor = async () => {
    if (!selectedColor) return;
    
    setIsSearching(true);
    setSearchColor(selectedColor);
    setSearchSimilarity(similarity);

    try {
      const currentUser = getCurrentUser();
      // Server-side pre-filter by colorFamily so we don't drag every
      // polish item across the app down to the browser. Requires the
      // `colorFamily` fieldOverride on items (COLLECTION_GROUP scope +
      // arrayConfig:CONTAINS) in firestore.indexes.json. Falls back to a
      // full scan when the picked hex doesn't map to any known family —
      // rare, and accepting the perf hit beats returning zero matches.
      const families = inferColorFamily(selectedColor);
      const itemsRef = collectionGroup(db, 'items');
      const q =
        families.length > 0
          ? query(
              itemsRef,
              where('itemType', '==', 'polish'),
              where('colorFamily', 'array-contains-any', families)
            )
          : query(itemsRef, where('itemType', '==', 'polish'));
      const snapshot = await getDocs(q);

      const matches: Map<string, { key: string; item: CollectionItem; similarity: number; collectors: Set<string> }> = new Map();
      // Fallback photo per polish, populated from EVERY item we see —
      // even ones that don't clear the similarity threshold. Handles the
      // case where the color-matching owners of a polish haven't uploaded
      // photos but a non-matching owner has. Without this, tiles render
      // photo-less even though a community photo exists.
      const fallbackPhotoByKey = new Map<string, string>();

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Owner-private items are hidden from every other viewer's color
        // search. Owner still sees their own private items in results.
        if (data.isPrivate === true && data.userId !== currentUser?.uid) return;
        if (!data.colors?.length && !data.primaryColor) return;

        const key = `${(data.brand || '').toLowerCase()}-${(data.name || '').toLowerCase()}`;
        if (!fallbackPhotoByKey.has(key) && Array.isArray(data.photos)) {
          const url =
            data.photos.find((p: any) => p?.isDefault && p?.url)?.url ??
            data.photos.find((p: any) => p?.url)?.url ??
            null;
          if (url) fallbackPhotoByKey.set(key, url);
        }

        // Check all colors in the item, not just the first one
        const itemColors = data.colors || [data.primaryColor];
        let bestSimilarity = 0;

        itemColors.forEach((itemColor: string) => {
          if (itemColor) {
            const sim = colorSimilarity(selectedColor, itemColor);
            if (sim > bestSimilarity) bestSimilarity = sim;
          }
        });

        // Use the adjustable similarity threshold
        if (bestSimilarity >= similarity) {
          const existing = matches.get(key);

          if (existing) {
            existing.collectors.add(data.userId);
            if (bestSimilarity > existing.similarity) {
              existing.similarity = bestSimilarity;
              existing.item = { id: doc.id, userId: data.userId, ...data } as CollectionItem;
            }
          } else {
            matches.set(key, {
              key,
              item: { id: doc.id, userId: data.userId, ...data } as CollectionItem,
              similarity: bestSimilarity,
              collectors: new Set([data.userId])
            });
          }
        }
      });

      const preliminaryResults: (DupeResult & { key: string })[] = Array.from(matches.values())
        .map(m => {
          let item = m.item;
          const hasPhoto = Array.isArray(item.photos) && item.photos.some((p) => p?.url);
          if (!hasPhoto) {
            const fallback = fallbackPhotoByKey.get(m.key);
            if (fallback) {
              item = { ...item, photos: [{ url: fallback, isDefault: true }] };
            }
          }
          return { item, similarity: m.similarity, collectorCount: m.collectors.size, key: m.key };
        })
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 50);

      // Supplementary slug-based lookup for polishes still missing a
      // photo after the color-family-scoped pass. Some collectors of the
      // SAME polish tag with a different color family (or none), so the
      // main filtered query misses their photos entirely. Uses the
      // `slug` COLLECTION_GROUP fieldOverride from firestore.indexes.json.
      const slugsMissingPhoto = preliminaryResults
        .filter((r) => !getDefaultPhoto(r.item))
        .map((r) => (r.item as CollectionItem & { slug?: string }).slug)
        .filter((s): s is string => !!s);
      if (slugsMissingPhoto.length > 0) {
        const supplementaryPhotos = new Map<string, string>();
        const CHUNK = 30;
        for (let i = 0; i < slugsMissingPhoto.length; i += CHUNK) {
          const chunk = slugsMissingPhoto.slice(i, i + CHUNK);
          try {
            const supRef = collectionGroup(db, 'items');
            const supSnap = await getDocs(query(supRef, where('slug', 'in', chunk)));
            supSnap.docs.forEach((doc) => {
              const data = doc.data() as Record<string, any>;
              if (data.isDeleted) return;
              // Owner-private items don't contribute supplementary photos
              // to other viewers' color-search results either.
              const ownerId = doc.ref.parent.parent?.id;
              if (data.isPrivate === true && ownerId !== currentUser?.uid) return;
              const slug: string | undefined = data.slug;
              if (!slug || supplementaryPhotos.has(slug)) return;
              if (Array.isArray(data.photos)) {
                const url =
                  data.photos.find((p: any) => p?.isDefault && p?.url)?.url ??
                  data.photos.find((p: any) => p?.url)?.url ??
                  null;
                if (url) supplementaryPhotos.set(slug, url);
              }
            });
          } catch (supErr) {
            console.warn('supplementary photo lookup failed', supErr);
          }
        }
        preliminaryResults.forEach((r) => {
          if (!getDefaultPhoto(r.item)) {
            const slug = (r.item as CollectionItem & { slug?: string }).slug;
            const found = slug ? supplementaryPhotos.get(slug) : undefined;
            if (found) {
              r.item = { ...r.item, photos: [{ url: found, isDefault: true }] };
            }
          }
        });
      }

      const sortedResults: DupeResult[] = preliminaryResults.map(({ key: _key, ...rest }) => rest);

      setResults(sortedResults);
    } catch (error) {
      console.error('Color search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const getDefaultPhoto = (item: CollectionItem) => {
    if (!item.photos?.length) return null;
    return item.photos.find(p => p.isDefault)?.url || item.photos[0]?.url;
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl">
          <Palette className="icon-base text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-glass font-body">
            Color Explorer
          </h2>
          <p className="text-sm text-glass-secondary">Find polishes by color across all collections</p>
        </div>
      </div>

      {/* Color Search Filter - reused component, open by default here */}
      <ColorSearchFilter
        selectedColor={selectedColor}
        onColorSelect={setSelectedColor}
        similarity={similarity}
        onSimilarityChange={setSimilarity}
        defaultOpen={true}
      />

      {/* Search Button */}
      <button
        onClick={searchByColor}
        disabled={!selectedColor || isSearching}
        className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
      >
        {isSearching ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Searching...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Search for Matching Polishes
          </>
        )}
      </button>

      {/* Results */}
      {isSearching ? (
        <div className="flex flex-col items-center justify-center py-12 text-glass-muted">
          <Loader2 className="w-8 h-8 animate-spin mb-3 text-cyan-500" />
          <p>Searching for matching polishes...</p>
        </div>
      ) : searchColor && results.length > 0 ? (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-glass-secondary mb-3">
            Found {results.length} matching polish{results.length !== 1 ? 'es' : ''} at {searchSimilarity}%+ similarity: <span className="text-glass-muted">(click to view details)</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
            {results.map((result) => (
              <button
                key={`${result.item.userId}-${result.item.id}`}
                onClick={() => setModalItem(result)}
                className="flex items-center gap-3 p-3 bg-slate-800/60 rounded-xl hover:bg-slate-700/60 transition-all text-left"
              >
                {getDefaultPhoto(result.item) ? (
                  // Photos here always come from user items — the Color
                  // Explorer bypasses the two-tier catalog rule so tiles
                  // have visual grounding. Icon-only badge attributes the
                  // photo without hiding half the thumbnail; full context
                  // lives in the tooltip and on the polish detail page.
                  <div className="relative w-12 h-12 flex-shrink-0">
                    <img src={getDefaultPhoto(result.item)!} alt={result.item.name} className="w-full h-full rounded-lg object-cover" />
                    <span
                      className="absolute top-0.5 left-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-slate-900/85 border border-slate-500/50"
                      title="Collector photo — not an official brand image"
                      aria-label="Collector photo — not an official brand image"
                    >
                      <Users className="w-2.5 h-2.5 text-slate-100" />
                    </span>
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: result.item.primaryColor || '#ccc' }}>
                    <Sparkles className="icon-sm text-white/70" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-glass truncate text-sm">{result.item.name}</h4>
                  <p className="text-xs text-glass-muted truncate">{result.item.brand}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs px-1.5 py-0.5 bg-cyan-500/20 text-cyan-300 rounded font-medium">
                      {Math.round(result.similarity)}% match
                    </span>
                    <span className="text-xs text-glass-muted flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {result.collectorCount}
                    </span>
                  </div>
                </div>
                {result.item.primaryColor && (
                  <div className="w-6 h-6 rounded-full border border-white shadow" style={{ backgroundColor: result.item.primaryColor }} />
                )}
                <ChevronRight className="w-4 h-4 text-glass-muted" />
              </button>
            ))}
          </div>
        </div>
      ) : searchColor && results.length === 0 ? (
        <div className="text-center py-12 text-glass-muted mt-6">
          <Palette className="icon-2xl mx-auto mb-3 opacity-30" />
          <p>No matches found at {searchSimilarity}% similarity</p>
          <p className="text-sm mt-1">Try lowering the similarity slider and searching again</p>
        </div>
      ) : (
        <div className="text-center py-12 text-glass-muted mt-6">
          <Palette className="icon-2xl mx-auto mb-3 opacity-30" />
          <p>Pick a color above, then click Search</p>
          <p className="text-sm mt-1">We'll find polishes with similar shades</p>
        </div>
      )}

      {/* Detail Modal */}
      {modalItem && (
        <DiscoveryItemModal
          item={modalItem.item}
          similarity={modalItem.similarity}
          collectorCount={modalItem.collectorCount}
          onClose={() => setModalItem(null)}
        />
      )}
    </div>
  );
}
